// File: src/pages/targets/TargetCreation.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Button,
  Input,
  Modal,
  ModalHeader,
  ModalBody,
  Badge,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ImDownload } from "react-icons/im";
import { FaFilter, FaPlus } from "react-icons/fa";
import { Eye, Edit, Trash2 } from "react-feather";

// ---- project imports (as per your note: everything is under ../import/) ----
import { CheckPermission } from "../import/CheckPermission";
import SuperAdminUI from "../import/SuperAdminUI";
import {
  Create_TargetList,
  Delete_targetINlist,
  _Get,
} from "../import/ApiEndPoint/ApiCalling";
import {
  exportDataToPDF,
  exportDataToExcel,
  convertDataCSVtoExcel,
  convertDataCsvToXml,
} from "../import/Downloader";

import TargetAssignedOne from "./TargetAssingnedOne";

// ---------- minimal inline styling (WarehouseList look) ----------
const injectOnce = () => {
  if (document.getElementById("targetcreation-inline-css")) return;
  const s = document.createElement("style");
  s.id = "targetcreation-inline-css";
  s.innerHTML = `
    .tw-card{ border:1px solid #e5e7eb; border-radius:14px; }
    .tw-card-lite{ border:1px solid #e5e7eb; border-radius:14px; background:#f4f7f6; }
    .tw-tablewrap{ width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff; }
    .tw-table{ width:100%; border-collapse:collapse; min-width:980px; }
    .tw-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:8px 10px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .tw-table tbody td{ padding:8px 10px; border-bottom:1px solid #f1f5f9; vertical-align:middle; text-align:center; font-size:14px; }
    .dl-menu{
      position:absolute; right:0; top:36px; z-index:10; border:1px solid rgb(8, 91, 245); background:#fff; border-radius:10px;
      overflow:hidden; min-width:140px; box-shadow:0 12px 24px rgba(0,0,0,.12);
    }
    .dl-item{ padding:10px 12px; cursor:pointer; font-weight:600; letter-spacing:.02em; }
    .dl-item:hover{ background:#f4f7f6; }
    .pick-box{
      border:1px solid #e5e7eb; border-radius:10px; max-height:320px; overflow:auto; background:#fff; padding:8px;
    }
    .pick-item{
      padding:8px 10px; border:1px solid #e5e7eb; border-radius:8px; margin-bottom:6px; background:#fff; cursor:pointer;
    }
  `;
  document.head.appendChild(s);
};

// safe helpers
const S = (v) => (v === null || v === undefined ? "" : String(v));
const N = (v) => (isFinite(+v) ? +v : 0);

// Column config
const BASE_COLS = [
  { key: "uid", header: "UID", get: (_r, i) => i + 1 },
  {
    key: "actions",
    header: "ACTIONS",
    get: () => "", // rendered separately
  },
  {
    key: "grandTotal",
    header: "TOTAL TARGET",
    get: (r) => r?.grandTotal,
  },
  {
    key: "fullName",
    header: "FULLNAME",
    get: (r) => r?.partyId?.CompanyName,
  },
  {
    key: "productAssigned",
    header: "PRODUCT ASSIGNED",
    get: (r) => (Array.isArray(r?.products) ? r.products.length : 0),
  },
  {
    key: "createdAt",
    header: "CREATED AT",
    get: (r) => S(r?.createdAt)?.split("T")[0],
  },
  {
    key: "updatedAt",
    header: "UPDATED AT",
    get: (r) => S(r?.updatedAt)?.split("T")[0],
  },
];

export default function TargetCreation({ ViewOneData }) {
  injectOnce();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [headerTitle] = useState("Target List");

  // permissions & master view
  const [insiderPerms, setInsiderPerms] = useState({});
  const [isMaster, setIsMaster] = useState(false);

  // search & downloads
  const [q, setQ] = useState("");
  const [dlOpen, setDlOpen] = useState(false);

  // modal: column chooser
  const [colsOpen, setColsOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const bufferRef = useRef([]);

  // modal: view assigned products
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);

  // mount / hydrate
  useEffect(() => {
    const perms = CheckPermission("Target Creation");
    setInsiderPerms(perms || {});

    const ud = JSON.parse(localStorage.getItem("userData") || "{}");
    setIsMaster(ud?.rolename?.roleName === "MASTER");

    // prefer passed data; else try to fetch
    if (Array.isArray(ViewOneData) && ViewOneData.length) {
      setRows(ViewOneData);
      hydrateColumns("TargetofParty");
    } else {
      // fallback fetch for current user (kept compact)
      const id = ud?._id;
      const db = ud?.database;
      if (id && db) {
        (async () => {
          setLoading(true);
          try {
            const res = await Create_TargetList(id, db);
            setRows(
              Array.isArray(res?.TargetCreation) ? res.TargetCreation : []
            );
            hydrateColumns("TargetList");
          } catch (e) {
            setRows([]);
            console.error(e);
          } finally {
            setLoading(false);
          }
        })();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ViewOneData]);

  const hydrateColumns = (storageKey) => {
    const raw = localStorage.getItem(storageKey);
    let keys = [];
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && typeof parsed[0] === "string")
          keys = parsed;
      } catch {}
    }
    if (!keys.length) keys = BASE_COLS.map((c) => c.key);
    setSelectedKeys(keys);
  };

  const colMap = useMemo(() => {
    const m = new Map();
    BASE_COLS.forEach((c) => m.set(c.key, c));
    return m;
  }, []);

  const visibleCols = useMemo(
    () => selectedKeys.map((k) => colMap.get(k)).filter(Boolean),
    [selectedKeys, colMap]
  );

  // filter rows by search term (across visible cols except actions)
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((r, i) =>
      visibleCols
        .filter((c) => c.key !== "actions")
        .some((c) => S(c.get(r, i)).toLowerCase().includes(term))
    );
  }, [rows, q, visibleCols]);

  // totals (optional quick KPI)
  const totalTarget = useMemo(
    () => filtered.reduce((a, r) => a + N(r?.grandTotal), 0),
    [filtered]
  );

  // ----- actions -----
  const onView = (row) => {
    setViewData(row);
    setViewOpen(true);
  };

  const onEdit = (row) => {
    // v6 navigate (instead of useHistory)
    navigate(`/app/AJGroup/account/EditTarget/${row?._id}`, {
      state: row,
      replace: false,
    });
  };

  const onDelete = (row) => {
    const id = row?._id;
    if (!id) return;
    // confirm
    // (keeping your swal flow for consistency)
    // eslint-disable-next-line no-undef
    swal("Warning", "Sure You Want to Delete it", {
      buttons: {
        cancel: "cancel",
        catch: { text: "Delete", value: "delete" },
      },
    }).then(async (value) => {
      if (value !== "delete") return;
      try {
        await Delete_targetINlist(id);
        setRows((prev) => prev.filter((x) => x?._id !== id));
      } catch (e) {
        console.error(e);
      }
    });
  };

  // ----- downloads -----
  const buildCSV = () => {
    const colsForCsv = visibleCols; // includes "ACTIONS" header but it's fine; we’ll skip values
    const head = colsForCsv.map((c) => c.header).join(",");
    const lines = filtered.map((r, i) =>
      colsForCsv
        .map((c) => {
          if (c.key === "actions") return ""; // no data
          return `"${S(c.get(r, i)).replace(/"/g, '""')}"`;
        })
        .join(",")
    );
    return [head, ...lines].join("\r\n");
  };
  const doPDF = async () => {
    const csv = buildCSV();
    await exportDataToPDF(csv, "TargetList");
  };
  const doCSV = () => {
    const csv = buildCSV();
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "TargetList.csv";
    document.body.appendChild(a);
    a.click();
    URL.revokeObjectURL(url);
    a.remove();
  };
  const doXLS = async () => {
    const csv = buildCSV();
    await convertDataCSVtoExcel(csv, "TargetList");
  };
  const doXLSX = async () => {
    const csv = buildCSV();
    await exportDataToExcel(csv, "TargetList");
  };
  const doXML = async () => {
    const csv = buildCSV();
    await convertDataCsvToXml(csv, "TargetList");
  };

  // ----- column chooser -----
  const openCols = () => {
    bufferRef.current = [];
    setSelectedIndex(-1);
    setColsOpen(true);
  };
  const onCheck = (checked, key) => {
    const buf = bufferRef.current.slice();
    if (checked) {
      if (!buf.includes(key)) buf.push(key);
    } else {
      const i = buf.indexOf(key);
      if (i >= 0) buf.splice(i, 1);
    }
    bufferRef.current = buf;
  };
  const addChecked = () => {
    const next = Array.from(new Set([...selectedKeys, ...bufferRef.current]));
    setSelectedKeys(next);
  };
  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };
  const up = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };
  const down = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };
  const applyCols = () => {
    // choose storage key based on where data came from
    const storageKey =
      Array.isArray(ViewOneData) && ViewOneData.length
        ? "TargetofParty"
        : "TargetList";
    localStorage.setItem(storageKey, JSON.stringify(selectedKeys));
    setColsOpen(false);
  };

  // ----- master (superadmin) switcher -----
  const handleParentSubmit = (e) => {
    e.preventDefault();
    const SuperAdmin = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null"
    );
    if (!SuperAdmin) return;
    const [id, db] = String(SuperAdmin).split(" ");
    if (!id || !db) return;
    (async () => {
      setLoading(true);
      try {
        const res = await Create_TargetList(id, db);
        setRows(Array.isArray(res?.TargetCreation) ? res.TargetCreation : []);
      } catch (e) {
        console.error(e);
        setRows([]);
      } finally {
        setLoading(false);
      }
    })();
  };
  const handleDropdownChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {/* Toolbar */}
      <Row style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card className="tw-card-lite" style={{ padding: 8 }}>
            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                {headerTitle}
              </div>

              <div style={{ flex: "1 1 240px" }} />

              {isMaster && (
                <div style={{ minWidth: 280 }}>
                  <SuperAdminUI
                    onDropdownChange={handleDropdownChange}
                    onSubmit={handleParentSubmit}
                  />
                </div>
              )}

              <div
                style={{
                  minWidth: 280,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search..."
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {insiderPerms?.Create && (
                <Button
                  color="primary"
                  onClick={() =>
                    navigate("/app/SoftNumen/account/CreateTarget")
                  }
                  style={{
                    fontWeight: 600,
                    textTransform: "uppercase",
                    height: 40,
                  }}
                >
                  <FaPlus size={14} /> &nbsp;Create Target
                </Button>
              )}

              {insiderPerms?.View && (
                <Button
                  outline
                  color="primary"
                  onClick={openCols}
                  title="Filter Columns"
                  style={{ height: 40 }}
                >
                  <FaFilter size={16} />
                </Button>
              )}

              {insiderPerms?.Download && (
                <div
                  onMouseEnter={() => setDlOpen(true)}
                  onMouseLeave={() => setDlOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {dlOpen && (
                    <div className="dl-menu">
                      <div className="dl-item" onClick={doPDF}>
                        .PDF
                      </div>
                      <div className="dl-item" onClick={doCSV}>
                        .CSV
                      </div>
                      <div className="dl-item" onClick={doXLS}>
                        .XLS
                      </div>
                      <div className="dl-item" onClick={doXLSX}>
                        .XLSX
                      </div>
                      <div className="dl-item" onClick={doXML}>
                        .XML
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Row>
        <Col sm="12">
          <Card className="tw-card" style={{ padding: 8 }}>
            {!insiderPerms?.View ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                You don’t have permission to view targets.
              </div>
            ) : filtered.length === 0 ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No targets found.
              </div>
            ) : (
              <>
                <div className="tw-tablewrap">
                  <table className="tw-table">
                    <thead>
                      <tr>
                        {visibleCols.map((c) => (
                          <th key={c.key}>{c.header}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((r, i) => (
                        <tr key={r?._id || i}>
                          {visibleCols.map((c) => {
                            if (c.key === "actions") {
                              return (
                                <td key="actions">
                                  <div
                                    style={{
                                      display: "flex",
                                      gap: 10,
                                      justifyContent: "center",
                                    }}
                                  >
                                    {insiderPerms?.View && (
                                      <Eye
                                        size={18}
                                        color="green"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => onView(r)}
                                        title="View"
                                      />
                                    )}
                                    {insiderPerms?.Edit && (
                                      <Edit
                                        size={18}
                                        color="blue"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => onEdit(r)}
                                        title="Edit"
                                      />
                                    )}
                                    {insiderPerms?.Delete && (
                                      <Trash2
                                        size={18}
                                        color="red"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => onDelete(r)}
                                        title="Delete"
                                      />
                                    )}
                                  </div>
                                </td>
                              );
                            }
                            if (c.key === "grandTotal") {
                              return (
                                <td key={c.key}>
                                  <Badge color="primary">
                                    {S(c.get(r, i))}
                                  </Badge>
                                </td>
                              );
                            }
                            return <td key={c.key}>{S(c.get(r, i))}</td>;
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: 16,
                    justifyContent: "flex-end",
                    paddingTop: 10,
                  }}
                >
                  <strong>Total Target: {totalTarget}</strong>
                </div>
              </>
            )}
          </Card>
        </Col>
      </Row>

      {/* Column chooser modal */}
      <Modal
        isOpen={colsOpen}
        toggle={() => setColsOpen(false)}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1000 }}
      >
        <ModalHeader toggle={() => setColsOpen(false)}>
          Change Fields
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="5" md="5" sm="12">
              <h5>Available Columns</h5>
              <div className="pick-box">
                {BASE_COLS.map((c) => (
                  <div key={c.key} className="pick-item">
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) => onCheck(e.target.checked, c.key)}
                      />
                      <span>{c.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addChecked}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="7" md="7" sm="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div className="pick-box" style={{ flex: 1 }}>
                  {selectedKeys.map((k, i) => {
                    const col = colMap.get(k);
                    if (!col) return null;
                    return (
                      <div
                        key={k}
                        className="pick-item"
                        style={{
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={up}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={down}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyCols}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>

      {/* View modal – assigned products */}
      <Modal
        isOpen={viewOpen}
        toggle={() => setViewOpen(false)}
        className="modal-dialog modal-xl"
        size="lg"
        backdrop={true}
        fullscreen={true}
      >
        <ModalHeader toggle={() => setViewOpen(false)}>
          View Details
        </ModalHeader>
        <ModalBody style={{ paddingTop: 0 }}>
          {viewData ? <TargetAssignedOne ViewData={viewData} /> : null}
        </ModalBody>
      </Modal>
    </>
  );
}
